import React, { useEffect, useRef } from 'react';

import {
  lang,
  rgba,
  scssVariables as variables,
  upperFirstCharAll,
  userFullName
} from 'src/helpers';
import { MCText, SearchTextField, Spinner, View } from 'src/components/basic';
import { ActivityLabel } from 'src/components/user/LastActivity';
import doctorsModel from '../doctorsModel';
import doctorModel from '../doctorModel';
import { remCss, remFontCss } from '../utils';
import ShortInfo from 'src/components/user/ShortInfo';
import LANG_CONST from 'src/helpers/lang/const';
import { Virtuoso } from 'react-virtuoso';
import constants from 'src/constants';
import AlertManager from 'src/components/alert';

const DoctorItem = ({ item, onDoctorItem }) => {
  doctorModel.use();

  const isSelected = doctorModel.state.doctor?.id === item.id;

  return (
    <View
      data-testid={'public-doctor-row'}
      data-doctor-id={item.id}
      data-selected={isSelected ? 'true' : 'false'}
      style={{
        backgroundColor: isSelected ? rgba(variables['colors-mc-primary'], 0.1) : 'white',
        paddingVertical: remCss(15),
        paddingHorizontal: remCss(20),
        flexDirection: 'row',
        gap: remCss(10),
        alignItems: 'center',
        cursor: 'pointer'
      }}
      onClick={() => {
        if (item.status === constants.general.userStatus.disabled) {
          AlertManager.popup(AlertManager.TYPES.INFO, {
            maxWidth: 450,
            close: false,
            title: lang.t(LANG_CONST.notice.disabled_clinic_doctor, {
              clinic: upperFirstCharAll(item.clinicName),
              doctor: upperFirstCharAll(userFullName(item))
            }),
            actionButton: {
              label: LANG_CONST.buttons.ok,
              action: (props) => props.hide()
            }
          });
        }
        onDoctorItem(item);
      }}
    >
      <ShortInfo
        user={item}
        isDisabledAccount={item.status === constants.general.userStatus.disabled}
        fullname={userFullName(item, item.isDoctor)}
        width={80}
        remFontCss={remFontCss}
        onlineState={item.connections > 0 ? 'online' : ''}
        medium={true}
        note={
          <MCText
            data-testid={'public-doctor-row-status'}
            size={18}
            remFontCss={remFontCss}
            numberOfLines={1}
            weight={400}
            primary
          >
            <ActivityLabel
              accountState={item.accountState}
              lastActivity={item.lastActivity}
              connections={item.connections}
            />
          </MCText>
        }
      />
    </View>
  );
};

export const Doctors = ({ onDoctorItem }) => {
  const [searching, setSearching] = React.useState(false);
  const listRef = useRef();
  doctorModel.use();
  doctorsModel.use();

  const items = doctorsModel.items();
  const loading = doctorsModel.refreshing();
  const searchQuery = doctorsModel.listInput('search');
  const noResults = doctorsModel.noResults();
  const selectedDoctor = doctorModel.state.doctor;

  useEffect(() => {
    if (!selectedDoctor && !!items?.length) {
      doctorModel.setStateSilent({ doctor: items[0] });
      doctorModel.loadDetail(items[0].id);
    } else if (!!items?.length && doctorsModel.startProcess) {
      const { doctor } = doctorsModel.startProcess;
      doctorsModel.startProcess = null;
      const findIndex = items.findIndex((item) => item.id === doctor.id);

      if (findIndex < 0) {
        doctorsModel.addItem(doctor, { unshift: true });
      } else {
        setTimeout(() => {
          if (listRef.current) {
            listRef.current.scrollToIndex(findIndex);
          }
        }, 100);
      }
    }
  }, [selectedDoctor, items]);

  return (
    <View data-testid={'public-doctors-list-panel'} style={{ width: '30%', maxWidth: remCss(400) }}>
      <View style={{ paddingRight: remCss(20) }}>
        <SearchTextField
          template={'2'}
          placeholder={'Search'}
          inputTestId={'public-doctors-search-input'}
          autoFocus={searching}
          onChange={(v) => {
            setSearching(true);
            doctorsModel.handleChangeSearch(v);
          }}
          onBlur={() => {
            setTimeout(() => {
              setSearching(false);
            }, 150);
          }}
          value={searchQuery}
        />
      </View>
      <Virtuoso
        ref={listRef}
        data={items}
        style={{ backgroundColor: 'white' }}
        components={{
          Header: () => (
            <View style={{ padding: remCss(10) }}>
              {(noResults || loading) && (
                <>
                  {noResults && !searchQuery && (
                    <div style={{ textAlign: 'center', padding: '1rem' }}>
                      {lang.t(LANG_CONST.text.no_results_yet)}
                    </div>
                  )}
                  {noResults && searchQuery && (
                    <div style={{ textAlign: 'center', padding: '1rem' }}>
                      {lang.t(LANG_CONST.text.nothing_found)}
                    </div>
                  )}
                </>
              )}
            </View>
          ),
          Footer: () => (
            <div style={{ textAlign: 'center', paddingTop: 20, flex: 1 }}>
              {loading && <Spinner />}
            </div>
          )
        }}
        endReached={() => {
          if (doctorsModel.hasMore() && !doctorsModel.refreshing()) doctorsModel.loadMore();
        }}
        itemContent={(index, item) => (
          <DoctorItem item={item} index={index} onDoctorItem={onDoctorItem} />
        )}
      />
    </View>
  );
};
